import React, { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "~/util/cn";

interface InputOtpProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

export const InputOTP: React.FC<InputOtpProps> = ({
  length = 6,
  value,
  onChange,
  error = false,
  onComplete,
  disabled = false,
  placeholder = "",
  className = "",
  autoFocus = true,
}) => {
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const normalizedValue = value.slice(0, length);

  const digits = Array(length)
    .fill("")
    .map((_, index) => normalizedValue[index] || "");

  const focusInput = useCallback(
    (index: number) => {
      if (
        index >= 0 &&
        index < length &&
        inputRefs.current[index] &&
        !disabled
      ) {
        inputRefs.current[index]?.focus();
        setFocusedIndex(index);
      }
    },
    [length, disabled],
  );

  const getNextFocusIndex = useCallback(() => {
    const firstEmptyIndex = digits.findIndex((digit) => digit === "");
    return firstEmptyIndex === -1 ? length - 1 : firstEmptyIndex;
  }, [digits, length]);

  const handleInputChange = (index: number, inputValue: string) => {
    if (disabled) return;

    const numericValue = inputValue.replace(/[^0-9]/g, "");

    if (numericValue.length <= 1) {
      const newDigits = [...digits];
      newDigits[index] = numericValue;
      const newValue = newDigits.join("");

      onChange(newValue);

      if (numericValue && index < length - 1) {
        focusInput(index + 1);
      }

      if (newValue.length === length && onComplete) {
        onComplete(newValue);
      }
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
    if (disabled) return;

    if (event.key === "Backspace") {
      event.preventDefault();
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = "";
        onChange(newDigits.join(""));
      } else if (index > 0) {
        const newDigits = [...digits];
        newDigits[index - 1] = "";
        onChange(newDigits.join(""));
        focusInput(index - 1);
      }
    } else if (event.key === "ArrowLeft") {
      if (index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      }
    } else if (event.key === "ArrowRight") {
      if (index < length - 1) {
        event.preventDefault();
        focusInput(index + 1);
      }
    } else if (event.key === "Delete") {
      event.preventDefault();
      const newDigits = [...digits];
      newDigits[index] = "";
      onChange(newDigits.join(""));
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    if (disabled) return;

    event.preventDefault();
    const pasteData = event.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "");
    const pasteValue = pasteData.slice(0, length);

    onChange(pasteValue);

    const nextIndex = Math.min(pasteValue.length, length - 1);
    setTimeout(() => focusInput(nextIndex), 0);

    if (pasteValue.length === length && onComplete) {
      onComplete(pasteValue);
    }
  };

  const handleContainerClick = () => {
    if (disabled) return;
    const nextIndex = getNextFocusIndex();
    focusInput(nextIndex);
  };

  useEffect(() => {
    if (autoFocus && !disabled) {
      const nextIndex = getNextFocusIndex();
      focusInput(nextIndex);
    }
  }, [autoFocus, disabled, focusInput, getNextFocusIndex]);

  useEffect(() => {
    if (focusedIndex === -1) return;

    const nextIndex = getNextFocusIndex();
    if (nextIndex !== focusedIndex) {
      setTimeout(() => focusInput(nextIndex), 0);
    }
  }, [value, focusedIndex, getNextFocusIndex, focusInput]);

  return (
    <div
      ref={containerRef}
      className={cn("flex items-center justify-center gap-2", className)}
      onClick={handleContainerClick}
      role="group"
      aria-label="OTP"
    >
      {Array(length)
        .fill(null)
        .map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digits[index]}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              "h-[3.25rem] w-10 px-1.5 py-2",
              "border border-neutral-200 bg-neutral-50",
              "rounded-lg shadow-md",
              "text-center text-lg font-semibold",
              "transition-all duration-200 ease-in-out",
              "cursor-pointer ",
              "focus:outline-none",
              error
                ? "border-error focus:border-error focus:ring-error"
                : "border-black/10 focus:border-primary-500 focus:ring-primary-200",
              disabled
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-mama-gray-200/20 hover:bg-mama-gray-200/30",
              digits[index] && "border-gray-400",
            )}
            onChange={({ target }) => handleInputChange(index, target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={handlePaste}
            onFocus={() => setFocusedIndex(index)}
            onBlur={() => setFocusedIndex(-1)}
          />
        ))}
    </div>
  );
};
